/**
 * URL state helpers for the Payment Assignment Metrics page.
 *
 * All shareable state (tab, period, date range, section collapse, toggles,
 * customer selections, bot view) is serialised into query parameters so the
 * current view can be copied and shared.
 */

import dayjs from 'dayjs'
import { getDefaultPaymentPeriod } from '../hooks/usePaymentAssignmentFilters'

// ─────────────────────────────────────────────────────────────────────────────
// Section IDs
// ─────────────────────────────────────────────────────────────────────────────

/** Stable string IDs for every collapsible section on the metrics page. */
export const METRIC_SECTION = {
  // Payment tab
  MAKERS: 'makers',
  ESCALATION: 'escalation',
  PRIORITY: 'priority',
  TICKET: 'ticket',
  BOT_STAT: 'botstat',
  UNPAID: 'unpaid',
  // Customer tab
  COMPANY: 'company',
  CARRIER: 'carrier',
} as const

export type MetricSectionId =
  (typeof METRIC_SECTION)[keyof typeof METRIC_SECTION]

/** Sections that start collapsed (all others default to expanded). */
const DEFAULT_COLLAPSED_SECTIONS: MetricSectionId[] = [
  METRIC_SECTION.ESCALATION,
]

const ALL_SECTION_IDS = new Set<string>(Object.values(METRIC_SECTION))

// ─────────────────────────────────────────────────────────────────────────────
// Show-all toggle keys (stored in `showAll` CSV param)
// ─────────────────────────────────────────────────────────────────────────────

export const SHOW_ALL_KEY = {
  MAKERS: 'makers',
  ESCALATION: 'escalation',
} as const

// ─────────────────────────────────────────────────────────────────────────────
// State interface
// ─────────────────────────────────────────────────────────────────────────────

export interface MetricsUrlStateInput {
  tab: string
  period: string
  fromDate?: dayjs.Dayjs
  toDate?: dayjs.Dayjs
  /** Quick-range label selected in the DateRangeFilter (e.g. "3 Days", "Yesterday"). */
  quickRange?: string
  /** Set of section IDs that are currently collapsed. */
  collapsed: Set<string>
  showAllMakers: boolean
  showAllEscalation: boolean
  companies: string[]
  carriers: string[]
  /** 'summary' | 'detail' */
  botView: string
  /** 'all' | carrier id number */
  botCarrierId: string | number
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

function getSearchParams(): URLSearchParams {
  return new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  )
}

function splitParam(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key)
  if (!raw) return []
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

// ─────────────────────────────────────────────────────────────────────────────
// Parse helpers (each reads from the live URL once)
// ─────────────────────────────────────────────────────────────────────────────

const VALID_TABS = new Set(['payment', 'customer', 'bot'])

export function parseMetricsTab(): string {
  const tab = getSearchParams().get('tab')
  return tab && VALID_TABS.has(tab) ? tab : 'payment'
}

const PERIOD_RE = /^\d{4}-(?:0[1-9]|1[0-2])$/

export function parseMetricsPeriod(): string {
  const raw = getSearchParams().get('period')
  return raw && PERIOD_RE.test(raw) ? raw : getDefaultPaymentPeriod()
}

export function parseMetricsDateRange(): {
  fromDate?: dayjs.Dayjs
  toDate?: dayjs.Dayjs
} {
  const params = getSearchParams()
  const from = params.get('from')
  const to = params.get('to')
  const fromDate = from ? dayjs(from) : undefined
  const toDate = to ? dayjs(to) : undefined
  return {
    fromDate: fromDate?.isValid() ? fromDate : undefined,
    toDate: toDate?.isValid() ? toDate : undefined,
  }
}

/**
 * Returns the Set of section IDs that should be collapsed.
 * When the `collapsed` param is absent we apply the default (escalation only).
 * When it is present (even as an empty string) we trust it exactly.
 */
export function parseMetricsCollapsed(): Set<MetricSectionId> {
  const params = getSearchParams()
  const raw = params.get('collapsed')
  if (raw === null) {
    // No param → use defaults
    return new Set<MetricSectionId>(DEFAULT_COLLAPSED_SECTIONS)
  }
  const ids = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => ALL_SECTION_IDS.has(s)) as MetricSectionId[]
  return new Set<MetricSectionId>(ids)
}

export function parseMetricsShowAll(): {
  showAllMakers: boolean
  showAllEscalation: boolean
} {
  const vals = splitParam(getSearchParams(), 'showAll')
  return {
    showAllMakers: vals.includes(SHOW_ALL_KEY.MAKERS),
    showAllEscalation: vals.includes(SHOW_ALL_KEY.ESCALATION),
  }
}

/** Returns the quick-range label stored in the URL, or undefined if absent. */
export function parseMetricsQuickRange(): string | undefined {
  return getSearchParams().get('range') ?? undefined
}

export function parseMetricsCompanies(): string[] {
  return splitParam(getSearchParams(), 'companies')
}

export function parseMetricsCarriers(): string[] {
  return splitParam(getSearchParams(), 'carriers')
}

export function parseMetricsBotState(): {
  botView: string
  botCarrierId: string | number
} {
  const params = getSearchParams()
  const botView = params.get('botView') === 'detail' ? 'detail' : 'summary'
  if (botView !== 'detail') return { botView: 'summary', botCarrierId: 'all' }
  const botCarrierParam = params.get('botCarrier')
  if (!botCarrierParam || botCarrierParam === 'all') {
    return { botView: 'detail', botCarrierId: 'all' }
  }
  const num = Number(botCarrierParam)
  return {
    botView: 'detail',
    botCarrierId: Number.isFinite(num) ? num : 'all',
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Serialize + sync
// ─────────────────────────────────────────────────────────────────────────────

export function buildMetricsQueryString(state: MetricsUrlStateInput): string {
  const params = new URLSearchParams()

  // Tab — only write when non-default
  if (state.tab && state.tab !== 'payment') params.set('tab', state.tab)

  // Period
  if (state.period) params.set('period', state.period)

  // Date range + quick-range label
  if (state.fromDate?.isValid()) {
    params.set('from', state.fromDate.format('YYYY-MM-DD'))
  }
  if (state.toDate?.isValid()) {
    params.set('to', state.toDate.format('YYYY-MM-DD'))
  }
  if (state.quickRange) {
    params.set('range', state.quickRange)
  }

  // Always write the `collapsed` param — even as an empty string — so that
  // `parseMetricsCollapsed` can distinguish "all expanded" (param present,
  // value empty) from "first load / no state yet" (param absent → defaults).
  params.set('collapsed', Array.from(state.collapsed).sort().join(','))

  // Show-all toggles
  const showAll: string[] = []
  if (state.showAllMakers) showAll.push(SHOW_ALL_KEY.MAKERS)
  if (state.showAllEscalation) showAll.push(SHOW_ALL_KEY.ESCALATION)
  if (showAll.length > 0) params.set('showAll', showAll.join(','))

  // Customer tab filters
  if (state.companies.length > 0) {
    params.set('companies', state.companies.join(','))
  }
  if (state.carriers.length > 0) {
    params.set('carriers', state.carriers.join(','))
  }

  // Bot tab state — only write when navigated to detail view
  if (state.botView === 'detail') {
    params.set('botView', 'detail')
    params.set('botCarrier', String(state.botCarrierId))
  }

  return params.toString()
}

/** Replaces the current URL (no history entry) with the serialised state. */
export function syncMetricsUrl(state: MetricsUrlStateInput): void {
  if (typeof window === 'undefined') return
  const queryString = buildMetricsQueryString(state)
  const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (newUrl !== currentUrl) {
    window.history.replaceState(window.history.state, '', newUrl)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience
// ─────────────────────────────────────────────────────────────────────────────

/** Derive a boolean expanded/collapsed map from the Set for easy prop passing. */
export function buildSectionExpandedMap(
  collapsed: Set<string>
): Record<MetricSectionId, boolean> {
  return Object.fromEntries(
    Object.values(METRIC_SECTION).map((id) => [id, !collapsed.has(id)])
  ) as Record<MetricSectionId, boolean>
}
